import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ArrowLeft, Plus, Star, Play, Mail } from 'lucide-react';
import { useGame } from '../context/GameContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AVATARS = ['🧒', '👦', '👧', '🧒🏻', '👦🏻', '👧🏻', '🧒🏽', '👦🏽', '👧🏽', '🧒🏿', '👦🏿', '👧🏿', '🦸', '🦸‍♀️', '🧙', '🧚', '🦄', '🐱', '🐶', '🐼', '🦁', '🐯', '🦊', '🐰'];

// Generate unique username suggestions
const generateUsernameSuggestions = (baseName) => {
  const suggestions = [];
  const numbers = Math.floor(Math.random() * 900) + 100;
  
  suggestions.push(`${baseName}${numbers}`);
  suggestions.push(`${baseName}_${Math.floor(Math.random() * 100)}`);
  suggestions.push(`${baseName}-${Math.floor(Math.random() * 100)}`);
  suggestions.push(`${baseName}.${Math.floor(Math.random() * 100)}`);
  
  return suggestions;
};

const ProfileSelection = () => {
  const navigate = useNavigate();
  const { setPlayerName } = useGame();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState('🧒');
  const [error, setError] = useState('');
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [nameTaken, setNameTaken] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [parentEmailFilter, setParentEmailFilter] = useState('');
  const [noProfilesFound, setNoProfilesFound] = useState(false);

  useEffect(() => {
    // Check if there's a parent email filter from returning player flow
    const emailFilter = localStorage.getItem('parentEmailFilter');
    if (emailFilter) {
      setParentEmailFilter(emailFilter);
      loadProfilesByEmail(emailFilter);
    } else {
      // No filter - redirect back to child setup
      navigate('/child-setup');
    }
  }, [navigate]);

  const loadProfilesByEmail = async (email) => {
    try {
      const response = await fetch(`${API_URL}/api/kids/profiles-by-email?parent_email=${encodeURIComponent(email)}`);
      if (response.ok) {
        const data = await response.json();
        const filteredProfiles = data.profiles || [];
        setProfiles(filteredProfiles);
        setNoProfilesFound(filteredProfiles.length === 0);
      } else {
        setProfiles([]);
        setNoProfilesFound(true);
      }
    } catch (error) {
      console.error('Error loading profiles:', error);
      setProfiles([]);
      setNoProfilesFound(true);
    } finally {
      setLoading(false);
    }
  };

  // Check if username is taken when user types
  const checkUsername = (name) => {
    setNewName(name);
    setError('');
    setNameTaken(false);
    setSuggestions([]);
    
    if (name.trim().length >= 2) {
      const existingProfile = profiles.find(p => p.player_name.toLowerCase() === name.trim().toLowerCase());
      if (existingProfile) {
        setNameTaken(true);
        setSuggestions(generateUsernameSuggestions(name.trim()));
      }
    }
  };

  const selectSuggestion = (suggestion) => {
    setNewName(suggestion);
    setNameTaken(false);
    setSuggestions([]);
  };

  const handleCreateProfile = async () => {
    if (!newName.trim()) {
      setError('Please enter a name!');
      return;
    }

    // Check if name already exists
    const existingProfile = profiles.find(p => p.player_name.toLowerCase() === newName.trim().toLowerCase());
    if (existingProfile) {
      setError('This name is already taken! Choose a suggestion or add numbers/special characters.');
      setNameTaken(true);
      setSuggestions(generateUsernameSuggestions(newName.trim()));
      return;
    }

    // Validate email - required for first-time signup
    if (!parentEmail.trim() || !parentEmail.includes('@')) {
      setError('Please enter a valid parent email address!');
      return;
    }

    // Store profile data temporarily (will be saved after payment)
    const pendingProfile = {
      player_name: newName.trim(),
      avatar: selectedAvatar,
      parent_email: parentEmail.trim()
    };
    localStorage.setItem('pendingProfile', JSON.stringify(pendingProfile));
    
    // Set player name for the session
    setPlayerName(newName.trim());
    localStorage.setItem('playerName', newName.trim());
    localStorage.setItem('playerAvatar', selectedAvatar);
    localStorage.setItem('userRole', 'child');
    
    // Go to payment - profile will be created after payment
    navigate('/payment');
  };

  const handleSelectProfile = async (profile) => {
    // Clear the email filter on successful login
    localStorage.removeItem('parentEmailFilter');
    
    // For returning users, just log them in directly (no password needed)
    setPlayerName(profile.player_name);
    localStorage.setItem('playerName', profile.player_name);
    localStorage.setItem('playerAvatar', profile.avatar || '🧒');
    localStorage.setItem('userRole', 'child');
    
    // Check subscription and navigate
    try {
      const response = await fetch(`${API_URL}/api/player/${encodeURIComponent(profile.player_name)}/subscription`);
      const data = await response.json();
      
      if (data.subscription_status === 'active') {
        navigate('/age-selection');
      } else {
        navigate('/payment');
      }
    } catch (error) {
      navigate('/payment');
    }
  };

  const handleGoBack = () => {
    localStorage.removeItem('parentEmailFilter');
    navigate('/child-setup');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div className="text-white text-2xl animate-pulse">Finding your profiles...</div>
      </div>
    );
  }

  // No profiles found for this email
  if (noProfilesFound && !showCreateForm) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Card className="max-w-md w-full p-8 bg-white/95 text-center" style={{ borderRadius: '24px' }}>
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Profiles Found</h2>
          <p className="text-gray-600 mb-6">
            No profiles are linked to <strong>{parentEmailFilter}</strong>
          </p>
          <div className="space-y-3">
            <Button
              onClick={handleGoBack}
              className="w-full py-4 text-lg font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white"
              style={{ borderRadius: '12px' }}
            >
              Create New Profile
            </Button>
            <Button
              onClick={handleGoBack}
              variant="outline"
              className="w-full py-4 text-lg"
              style={{ borderRadius: '12px' }}
            >
              Try Different Email
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8" style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <Button
            onClick={handleGoBack}
            variant="ghost"
            className="text-lg font-semibold text-white hover:scale-110 transition-transform"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Who's Playing? 🎮
          </h1>
          <p className="text-xl text-white/80">
            Pick your profile or create a new one!
          </p>
        </div>

        {/* Create New Profile Form */}
        {showCreateForm && (
          <Card className="p-6 mb-8 bg-white/95 backdrop-blur-sm" style={{ borderRadius: '24px' }}>
            <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">
              Create Your Profile! ✨
            </h3>
            
            {/* Avatar Selection */}
            <div className="text-center mb-4">
              <div 
                className="text-6xl cursor-pointer inline-block hover:scale-110 transition-transform"
                onClick={() => setShowAvatarPicker(!showAvatarPicker)}
              >
                {selectedAvatar}
              </div>
              <p className="text-sm text-gray-500 mt-1">Tap to change avatar</p>
            </div>
            
            {showAvatarPicker && (
              <div className="flex flex-wrap justify-center gap-2 mb-4 p-3 bg-gray-100 rounded-xl">
                {AVATARS.map(avatar => (
                  <button
                    key={avatar}
                    onClick={() => {
                      setSelectedAvatar(avatar);
                      setShowAvatarPicker(false);
                    }}
                    className={`text-3xl p-2 rounded-lg hover:bg-purple-100 transition-colors ${
                      selectedAvatar === avatar ? 'bg-purple-200 ring-2 ring-purple-500' : ''
                    }`}
                  >
                    {avatar}
                  </button>
                ))}
              </div>
            )}

            <div className="space-y-4">
              {/* Username Input */}
              <div>
                <Input
                  type="text"
                  placeholder="Your unique name..."
                  value={newName}
                  onChange={(e) => checkUsername(e.target.value)}
                  className={`text-xl p-4 text-center border-2 ${nameTaken ? 'border-red-400 focus:border-red-500' : 'border-purple-300 focus:border-purple-500'}`}
                  style={{ borderRadius: '12px' }}
                />
                
                {/* Name Taken Warning & Suggestions */}
                {nameTaken && (
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                    <p className="text-yellow-700 font-semibold text-center mb-2">
                      ⚠️ "{newName}" is already taken!
                    </p>
                    <p className="text-sm text-yellow-600 text-center mb-3">
                      Try one of these or add numbers/characters:
                    </p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {suggestions.map((suggestion, idx) => (
                        <button
                          key={idx}
                          onClick={() => selectSuggestion(suggestion)}
                          className="px-3 py-2 bg-white border-2 border-purple-300 rounded-lg text-purple-700 font-semibold hover:bg-purple-100 transition-colors"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Parent Email - Required */}
              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm font-semibold text-gray-700 text-center mb-2">
                  👨‍👩‍👧 Parent's Email <span className="text-red-500">*</span>
                </p>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="parent@email.com"
                    value={parentEmail}
                    onChange={(e) => { setParentEmail(e.target.value); setError(''); }}
                    className="text-lg p-4 pl-12 text-center border-2 border-blue-300 focus:border-blue-500"
                    style={{ borderRadius: '12px' }}
                  />
                </div>
              </div>

              {error && (
                <p className="text-red-500 text-center font-semibold">{error}</p>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowCreateForm(false);
                    setError('');
                    setNewName('');
                    setParentEmail('');
                    setNameTaken(false);
                    setSuggestions([]);
                  }}
                  variant="outline"
                  className="flex-1 py-4 text-lg"
                  style={{ borderRadius: '12px' }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateProfile}
                  disabled={nameTaken || !newName.trim() || !parentEmail.trim()}
                  className="flex-1 py-4 text-lg font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white disabled:opacity-50"
                  style={{ borderRadius: '12px' }}
                >
                  Create Profile! 🎉
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Profiles Grid */}
        {!showCreateForm && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
            {/* Existing Profiles */}
            {profiles.map(profile => (
              <Card
                key={profile.player_id}
                onClick={() => handleSelectProfile(profile)}
                className="p-6 text-center cursor-pointer transition-all hover:scale-105 hover:shadow-xl bg-white/95"
                style={{ borderRadius: '20px' }}
              >
                <div className="text-5xl mb-2">{profile.avatar || '🧒'}</div>
                <h3 className="font-bold text-lg text-gray-800 truncate">{profile.player_name}</h3>
                <div className="flex items-center justify-center gap-1 text-yellow-600 mt-2">
                  <Star className="w-4 h-4 fill-yellow-400" />
                  <span className="font-semibold">{profile.points || 0}</span>
                </div>
                {profile.has_progress && (
                  <div className="mt-2 text-xs text-green-600 font-semibold flex items-center justify-center gap-1">
                    <Play className="w-3 h-3" />
                    Continue Playing
                  </div>
                )}
              </Card>
            ))}

            {/* Create New Profile Card */}
            <Card
              onClick={() => {
                setShowCreateForm(true);
                setError('');
              }}
              className="p-6 text-center cursor-pointer transition-all hover:scale-105 hover:shadow-xl bg-gradient-to-br from-purple-100 to-pink-100 border-2 border-dashed border-purple-300"
              style={{ borderRadius: '20px' }}
            >
              <div className="text-5xl mb-2">
                <Plus className="w-12 h-12 mx-auto text-purple-500" />
              </div>
              <h3 className="font-bold text-lg text-purple-700">New Profile</h3>
              <p className="text-sm text-purple-500 mt-1">Create one!</p>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileSelection;
